import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useDataView } from "../../contexts/data.view.context";
import { useConfirmedAddSubmit } from "../shared/useConfirmedAddSubmit";
import ConfirmAddDialog from "../util/confirmAddDialog";
import AddFormLayout from "../shared/AddFormLayout";
import SectionGrid from "../shared/SectionGrid";
import { useSectionedForm } from "../shared/useSectionedForm";

const FORM_SECTIONS = [
  {
    id: "basic",
    label: "Basic info",
    fields: ["API_ID", "MOD", "PATH", "NAME"],
  },
  {
    id: "operation",
    label: "Operation & state",
    fields: ["OPERATION", "STATE"],
  },
  {
    id: "reference",
    label: "Reference data",
    fields: ["REF_TYPE", "REF_ID", "SECURITY_POLICY", "SAMPLE_DATA"],
  },
];

const APIConfigForm = () => {
  const validationSchema = Yup.object().shape({
    API_ID: Yup.string().required("API ID is required"),
    MOD: Yup.string().required("MOD is required"),
    PATH: Yup.string().required("Path is required"),
    OPERATION: Yup.string().required("Operation is required"),
    STATE: Yup.string().required("State is required"),
    NAME: Yup.string().required("Name is required"),
    REF_TYPE: Yup.string().nullable().notRequired().default(""),
    REF_ID: Yup.string().nullable().notRequired().default(""),
    SECURITY_POLICY: Yup.string().nullable().notRequired().default(""),
    SAMPLE_DATA: Yup.string().nullable().notRequired().default(""),
    REF_DATA: Yup.string().nullable().notRequired().default(""),
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { sections, activeId, setActiveId, isFirst, isLast, goNext, goPrev } =
    useSectionedForm(FORM_SECTIONS, errors);

  const navigate = useNavigate();
  const { fetchAPIConfigsFromAllEnv } = useDataView();
  const operations = ["GET", "POST", "PUT", "DELETE", "PATCH"];
  const stateItems = ["Active", "Disabled"];
  const refTypes = [
    "AWARDCO",
    "CTPO",
    "DataQuery",
    "ESB",
    "FileQuery",
    "INCYTE",
  ];

  const { onSubmit, confirmDialogProps } = useConfirmedAddSubmit({
    resourcePath: "integrations/cfg/apiconfigs",
    notificationTitle: "Api Config",
    fetchFn: fetchAPIConfigsFromAllEnv,
    navigate,
    navigateTo: "/APIConfigs",
  });

  const handleNext = async () => {
    const current = sections[sections.findIndex((s) => s.id === activeId)];
    const valid = await trigger(current.fields);
    if (valid) goNext();
  };

  return (
    <>
      <AddFormLayout
        title="Add API config"
        sections={sections}
        activeSectionId={activeId}
        onSectionChange={setActiveId}
        onBack={() => navigate(-1)}
        secondaryLabel={isFirst ? undefined : "Previous"}
        onSecondaryClick={goPrev}
        primaryLabel={isLast ? "Add" : "Next section"}
        onPrimaryClick={isLast ? handleSubmit(onSubmit) : handleNext}
      >
        {activeId === "basic" && (
          <SectionGrid>
            <TextField
              required
              label="API_ID"
              size="small"
              fullWidth
              {...register("API_ID")}
              error={!!errors.API_ID}
              variant="outlined"
            />
            <TextField
              required
              label="MOD"
              size="small"
              fullWidth
              {...register("MOD")}
              error={!!errors.MOD}
              variant="outlined"
            />
            <TextField
              required
              label="PATH"
              size="small"
              fullWidth
              {...register("PATH")}
              error={!!errors.PATH}
              variant="outlined"
            />
            <TextField
              required
              label="NAME"
              size="small"
              fullWidth
              {...register("NAME")}
              error={!!errors.NAME}
              variant="outlined"
            />
          </SectionGrid>
        )}

        {activeId === "operation" && (
          <SectionGrid>
            <TextField
              select
              label="OPERATION"
              size="small"
              fullWidth
              {...register("OPERATION")}
              error={!!errors.OPERATION}
              variant="outlined"
            >
              {operations.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="STATE"
              size="small"
              fullWidth
              {...register("STATE")}
              error={!!errors.STATE}
              variant="outlined"
            >
              {stateItems.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </SectionGrid>
        )}

        {activeId === "reference" && (
          <SectionGrid>
            <TextField
              select
              label="REF_TYPE"
              size="small"
              fullWidth
              {...register("REF_TYPE")}
              error={!!errors.REF_TYPE}
              variant="outlined"
            >
              {refTypes.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="REF_ID"
              size="small"
              fullWidth
              {...register("REF_ID")}
              error={!!errors.REF_ID}
              variant="outlined"
            />
            <TextField
              label="SECURITY_POLICY"
              size="small"
              fullWidth
              {...register("SECURITY_POLICY")}
              error={!!errors.SECURITY_POLICY}
              variant="outlined"
            />
            <TextField
              label="SAMPLE_DATA"
              size="small"
              fullWidth
              {...register("SAMPLE_DATA")}
              error={!!errors.SAMPLE_DATA}
              variant="outlined"
            />
          </SectionGrid>
        )}
      </AddFormLayout>

      <ConfirmAddDialog {...confirmDialogProps} itemLabel="API config" />
    </>
  );
};

export default APIConfigForm;
